import { portalFetch } from '../../shared/auth/portalSession';
import { API_BASE_URL } from './config';
import { Holiday } from '../types';
import { getAuthHeaders, requireAuthentication } from './authUtils';

export interface HolidayApiPayload {
  date: string;
  description?: string;
}

export interface HolidayApiResponse {
  holiday_id: string;
  date: string;
  description?: string;
  created_at?: string;
}

// Helper to convert API response to frontend Holiday type
function convertApiToHoliday(apiHoliday: HolidayApiResponse): Holiday {
  return {
    id: apiHoliday.holiday_id,
    date: apiHoliday.date,
    description: apiHoliday.description,
  };
}

// Helper to convert frontend Holiday to API payload
function convertHolidayToApi(holiday: Omit<Holiday, 'id'>): HolidayApiPayload {
  return {
    date: holiday.date,
    description: holiday.description,
  };
}

export async function getHolidaysApi(): Promise<Holiday[]> {
  requireAuthentication();
  
  try {
    const response = await portalFetch('admin', `${API_BASE_URL}/holidays`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch holidays: ${response.statusText}`);
    }

    const data: { holidays: HolidayApiResponse[]; count: number } = await response.json();
    return data.holidays.map(convertApiToHoliday);
  } catch (error) {
    console.error('Error fetching holidays:', error);
    throw error;
  }
}

export async function addHolidayApi(holiday: Omit<Holiday, 'id'>): Promise<Holiday> {
  requireAuthentication();
  
  try {
    const payload = convertHolidayToApi(holiday);
    
    const response = await portalFetch('admin', `${API_BASE_URL}/holidays`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`Failed to add holiday: ${response.statusText}`);
    }

    const data: HolidayApiResponse = await response.json();
    return convertApiToHoliday(data);
  } catch (error) {
    console.error('Error adding holiday:', error);
    throw error;
  }
}

export async function deleteHolidayApi(id: string): Promise<void> {
  requireAuthentication();
  
  try {
    const response = await portalFetch('admin', `${API_BASE_URL}/holidays/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to delete holiday: ${response.statusText}`);
    }
  } catch (error) {
    console.error('Error deleting holiday:', error);
    throw error;
  }
}
